import { Component, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgIf } from '@angular/common';
import { MatError } from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import {TranslateModule, TranslateService} from '@ngx-translate/core';

import {
  ButtonSize,
  ButtonState,
  ButtonType,
  InputSize,
  InputState
} from '../../../../../../assets/types/dtoTypes';
import { RegistrationPageService } from '../../registration-page.service';
import { SocialAuthService } from '../../../../../shared/services/social-auth.service';
import { AuthenticationService } from '../../../../../shared/services/authentication.service';
import { ShowPopupService } from '../../../../../shared/services/show-popup.service';
import {
  BreakpointsService,
  BIPPERZ_BREAKPOINTS
} from '../../../../../shared/services/breakpoint.service';
import { InputComponent } from '../../../../../shared/input/input.component';
import { ButtonComponent } from '../../../../../shared/button/button.component';
import {AppPolicyModalComponent} from "../../../../../modals/app-policy-modal/app-policy-modal.component";

@Component({
  selector: 'app-email-form',
  templateUrl: './email-form.component.html',
  styleUrl: './email-form.component.scss',
  imports: [
    ReactiveFormsModule,
    InputComponent,
    TranslateModule,
    ButtonComponent,
    MatError,
    RouterLink,
    NgIf
  ],
  standalone: true
})
export class EmailFormComponent implements OnInit {
  private readonly breakpointsService = inject(BreakpointsService);
  private readonly fb = inject(FormBuilder);
  private readonly registrationPageService = inject(RegistrationPageService);
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly authService = inject(AuthenticationService);
  private readonly popupService = inject(ShowPopupService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);

  isSocialLoading = false;

  form!: FormGroup;
  protected readonly InputState = InputState;
  protected readonly InputSize = InputSize;
  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonState = ButtonState;
  protected readonly ButtonType = ButtonType;
  protected isMobile = false;

  ngOnInit(): void {
    this.breakpointsService
      .observe([BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down])
      .subscribe((result) => {
        this.isMobile = result.s || result.m_down;
      });

    this.form = this.fb.group({
      firstname: new FormControl('', [Validators.required, this.noWhitespaceValidator]),
      familyname: new FormControl('', [Validators.required, this.noWhitespaceValidator]),
      email: new FormControl('', [Validators.required, Validators.email]),
      termsAccepted: new FormControl(false, [Validators.requiredTrue])
    });
  }

  isInvalidAndTouched(field: string): boolean {
    const formField = this.form.get(field);
    return !!formField && formField.invalid && formField.touched;
  }

  noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const isWhitespace = (control.value || '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  openPasswordForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const firstname = this.form.get('firstname')?.value;
    const familyname = this.form.get('familyname')?.value;
    const emailValue = this.form.get('email')?.value;
    this.registrationPageService.checkEmailUsage(emailValue).subscribe({
      next: ({ used, verified }) => {
        // Only a verified account blocks re-registration. An unverified email means the user
        // never finished email verification — let them continue instead of trapping them on
        // "email already registered". The register step then resends the code and routes to
        // the verification screen (or reports VERIFICATION_PENDING, handled downstream).
        if (used && verified) {
          this.popupService.showPopup({
            status: 'failure',
            translationKey: 'job_details.popup.application.emailAlreadyRegistered',
            isBackToLogin: true
          });
          return;
        }
        this.registrationPageService.sendEmailAndUsername(emailValue, firstname, familyname);
        this.registrationPageService.openForm('email_form');
      },
      error: (error) => {
        const message = (
          error?.error?.message ??
          error?.error?.error ??
          error?.message ??
          ''
        ).toUpperCase();

        if (message.includes('EMAIL_TAKEN')
          || message.includes('ALREADY REGISTERED')
          || message.includes('DUPLICATE')) {
          this.popupService.showPopup({
            status: 'failure',
            translationKey: 'job_details.popup.application.emailAlreadyRegistered',
            isBackToLogin: true
          });
          return;
        }

        this.popupService.showPopup({
          status: 'failure',
          translationKey: 'job_details.popup.application.generalError',
          isBackToLogin: false
        });
      }
    });
  }

  getPolicyContent( policy: string ): { title: string, content: string } {
    const titles = {
      terms_of_services: this.translate.instant( 'footer.terms_of_services' ),
      privacy_policy: this.translate.instant( 'footer.privacy_policy' ),
      impressum: this.translate.instant( 'footer.impressum' ),
      about: this.translate.instant( 'footer.about' ),
    };

    const contentKey = `policy.${policy}`;

    let content = '';
    this.translate.get( contentKey ).subscribe( ( translated: string ) => {
      content = translated;
    } );

    return {
      title: titles[policy] || '',
      content,
    };
  }

  openPolicyModal( policy: string ) {
    const policyData = this.getPolicyContent( policy );
    this.dialog.open( AppPolicyModalComponent, { data: policyData } );
  }

  registerWithGoogle(): void {
    this.socialAuthService.registerWithGoogle('USER');
  }

  registerWithApple(): void {
    this.isSocialLoading = true;
    localStorage.setItem('socialRegistrationProvider', 'apple');
    this.socialAuthService.loginWithApple('USER', true).subscribe({
      next: (data) => {
        this.authService.setAccessToken(data.accessToken);
        this.registrationPageService.openForm('social_signup_consent');
        this.isSocialLoading = false;
      },
      error: (error) => {
        const message = (
          error?.error?.message ??
          error?.error?.error ??
          error?.message ??
          ''
        ).toUpperCase();
        const serializedError = JSON.stringify(error ?? {}).toUpperCase();
        const errorSignature = `${message} ${serializedError}`;

        if (errorSignature.includes('ACCOUNT_EXISTS_WITH_GOOGLE')) {
          this.popupService.showPopup({
            status: 'failure',
            translationKey: 'job_details.popup.application.accountExistsWithGoogle',
            isBackToLogin: true
          });
        } else if (errorSignature.includes('ACCOUNT_EXISTS_WITH_APPLE')
          || errorSignature.includes('APPLE_ACCOUNT_ALREADY_EXISTS')) {
          this.popupService.showPopup({
            status: 'failure',
            translationKey: 'job_details.popup.application.accountExistsWithApple',
            isBackToLogin: true
          });
        } else if (errorSignature.includes('ACCOUNT_EXISTS_WITH_PASSWORD')) {
          this.popupService.showPopup({
            status: 'failure',
            translationKey: 'job_details.popup.application.accountExistsWithPassword',
            isBackToLogin: true
          });
        } else {
          this.popupService.showPopup({
            status: 'failure',
            translationKey: 'job_details.popup.application.generalError',
            isBackToLogin: false
          });
        }
        localStorage.removeItem('socialRegistrationProvider');
        this.isSocialLoading = false;
      },
    });
  }
}
